import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CFormInput,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CRow,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilCalendar, cilCheckCircle, cilPencil, cilTrash, cilX } from "@coreui/icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/sankey";

import { apiClient } from "services/Api";
import { jwtDecode } from "jwt-decode";
import DataTableComponent from "components/DataTableComponent";
import ToastNotification from "components/globals/ToastNotification";
import TextEditor from "components/forms/TextEditor";
import AlertNotification, {
  triggerAlert,
} from "components/globals/AlertNotification";
import TextFieldCell from "components/TextFieldCell";

const ImprovementAction = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [improvementActions, setImprovementActions] = useState([]);
  const location = useLocation();
  const [toast, setToast] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Default to current year
  const [availableYears, setAvailableYears] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [sankey, setSankey] = useState(null);
  const [isEditing] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");
  const [postponeModalVisible, setPostponeModalVisible] = useState(false);
  const [previewImprovementAction, setPreviewImprovementAction] = useState(null);

  const handleDownloadExcel = async () => {
    try {
      const response = await apiClient.get(
        `/improvement-actions/?year=${selectedYear}&export=excel`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `acoes_melhoria_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setToast({
        title: "Exportação",
        message: "Erro ao exportar Excel",
        type: "error",
      });
    }
  };

  const token = localStorage.getItem("access_token");
  const decodedToken = jwtDecode(token);
  const is_director = decodedToken.is_director;
  const user_id = decodedToken.user_id;

  const handleConclude = async (actionId) => {
    try {
      await apiClient.post(`/improvement-actions/${actionId}/conclude/`, {});
      triggerAlert("Ação de Melhoria concluída com sucesso!", "success");
      window.location.reload();
    } catch (error) {
      setToast({
        title: "Ação de Melhoria",
        message: "Erro ao concluir Ação de Melhoria",
        type: "error",
      });
    }
  };

  // Handle postponing an action
  const handlePostpone = async () => {
    if (!selectedRow || !newDate || !reason) {
      setToast({ message: "Preencha todos os campos!", type: "error" });
      return;
    }

    try {
      await apiClient.post(`/improvement-actions/${selectedRow.id}/postpone/`, {
        new_deadline: newDate,
        reason,
      });
      triggerAlert("Ação de Melhoria prorrogada com sucesso!", "success");

      window.location.reload(); // Refresh data
    } catch (error) {
      setToast({
        title: "Ação de Melhoria",
        message: "Erro a prorrogar a ação.",
        type: "error",
      });
    }
  };

  // Open postpone modal
  const openPostponeModal = (row) => {
    setSelectedRow(row);
    setPostponeModalVisible(true);
  };

  useEffect(() => {
    const fetchImprovementActions = async (year = selectedYear) => {
      try {
        const response = await apiClient.get(
          `/improvement-actions/?year=${year}`,
        );
        setImprovementActions(response.data.improvement_actions);
        setPreviewImprovementAction((current) =>
          current
            ? response.data.improvement_actions.find(
                (improvementAction) => improvementAction.id === current.id,
              ) || null
            : null,
        );
        setAvailableYears(response.data.available_years);
        setKpis(response.data.kpis || null);
        setSankey(response.data.sankey || null);
      } catch (error) {
        console.error("Error fetching improvement-actions:", error);
      }
    };

    fetchImprovementActions(selectedYear);
  }, [id, location.pathname, selectedYear]);

  const handleDelete = async (improvementActionId) => {
    try {
      await apiClient.delete(`/improvement-actions/${improvementActionId}/`);
      setImprovementActions((current) =>
        current.filter(
          (improvementAction) => improvementAction.id !== improvementActionId,
        ),
      );
      setPreviewImprovementAction((current) =>
        current?.id === improvementActionId ? null : current,
      );
    } catch (error) {
      console.error("Error deleting improvement action:", error);
    }
  };

  const columns = [
    { name: "Código", selector: (row) => row.code, sortable: true },
    {
      name: "Ação",
      cell: (row) => (
        <TextFieldCell
          html={row.action}
          onClick={() => setPreviewImprovementAction(row)}
        />
      ),
      grow: 3,
    },
    { name: "Prazo", selector: (row) => row.deadline, sortable: true },
    {
      name: "Data Conclusão",
      selector: (row) => row.conclusion_date,
      sortable: true,
    },
    {
      name: "Data Avaliação",
      selector: (row) => row.evaluation_date,
      sortable: true,
    },
    { name: "Avaliação", selector: (row) => row.efficiency, sortable: true },
  ];

  const buildSankeyOptions = (links) => ({
    chart: {
      height: 320,
      spacing: [6, 6, 6, 6],
      backgroundColor: "transparent",
    },
    title: { text: null },
    credits: { enabled: false },
    exporting: { enabled: false },
    tooltip: {
      pointFormat: "<b>{point.weight}</b>",
    },
    series: [
      {
        type: "sankey",
        data: links || [],
        name: "Fluxo de Ações",
        nodeWidth: 24,
        nodePadding: 20,
        colorByPoint: true,
      },
    ],
  });

  return (
    <>
      <ToastNotification toast={toast} setToast={setToast} />
      <AlertNotification />
      <CRow className="g-3 mb-4">
        <CCol sm={6} lg={4}>
          <CCard className="surveillance-kpi kpi-total h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Ano
              </div>
              <div className="fs-4 fw-semibold">{selectedYear}</div>
              <div className="text-medium-emphasis small">
                Período selecionado
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="surveillance-kpi kpi-completion h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Nº de ações
              </div>
              <div className="fs-4 fw-semibold">{kpis?.total ?? 0}</div>
              <div className="text-medium-emphasis small">Total registado</div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="surveillance-kpi kpi-pending h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Eficazes
              </div>
              <div className="fs-4 fw-semibold">
                {kpis?.effective_rate ?? 0}%
              </div>
              <div className="text-medium-emphasis small">
                {kpis?.effective_count ?? 0} ações
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow className="g-3 mb-4">
        <CCol>
          <CCard className="h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small mb-2">
                Fluxo das ações de melhoria
              </div>
              {sankey?.links?.length ? (
                <HighchartsReact
                  highcharts={Highcharts}
                  options={buildSankeyOptions(sankey.links)}
                />
              ) : (
                <div className="text-medium-emphasis small">
                  Sem dados no período selecionado.
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CCard>
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Ações de Melhoria</h3>
          <CButton color="success" onClick={() => handleDownloadExcel()}>
            Exportar Excel
          </CButton>
        </CCardHeader>
        <CCardBody>
          <DataTableComponent
            columns={columns}
            data={improvementActions}
            years={availableYears} // Optional
            selectedYear={selectedYear} // Optional
            onYearChange={(newYear) => setSelectedYear(newYear)} // Optional
            onRowClicked={setPreviewImprovementAction}
          />
        </CCardBody>
      </CCard>

      {previewImprovementAction && (
        <div
          style={{ position: "fixed", inset: 0, background: "transparent", zIndex: 1040 }}
          onClick={() => setPreviewImprovementAction(null)}
          aria-hidden="true"
        />
      )}

      <COffcanvas
        placement="end"
        visible={Boolean(previewImprovementAction)}
        onHide={() => setPreviewImprovementAction(null)}
        backdrop={false}
        scrollable
        scroll
        style={{ width: "560px", maxWidth: "95vw" }}
      >
        <COffcanvasHeader className="border-bottom">
          <COffcanvasTitle className="d-flex align-items-center me-auto">
            <div>
              <div className="fw-semibold">Ação de Melhoria</div>
              <div className="text-medium-emphasis small">
                {previewImprovementAction?.code || "-"}
              </div>
            </div>
          </COffcanvasTitle>
          <div className="d-flex align-items-center gap-2">
            {previewImprovementAction && (
              <CButton
                color="info"
                variant="outline"
                size="sm"
                onClick={() => navigate(`/acoes-melhoria/${previewImprovementAction.id}`)}
              >
                <CIcon icon={cilPencil} className="me-1" />
                {!isEditing ||
                (!previewImprovementAction.responsible_members.includes(user_id) &&
                  !is_director)
                  ? "Editar"
                  : "Ver"}
              </CButton>
            )}
            {previewImprovementAction &&
              (previewImprovementAction.responsible_members.includes(user_id) ||
                is_director) &&
              previewImprovementAction.approved === true &&
              ["Em curso", "", null].includes(previewImprovementAction.efficiency) && (
                <>
                  <CButton
                    color="success"
                    size="sm"
                    className="text-white"
                    onClick={() => handleConclude(previewImprovementAction.id)}
                  >
                    <CIcon icon={cilCheckCircle} className="me-1" />
                    Concluir
                  </CButton>
                  <CButton
                    color="warning"
                    size="sm"
                    onClick={() => openPostponeModal(previewImprovementAction)}
                  >
                    <CIcon icon={cilCalendar} className="me-1" />
                    Prorrogar
                  </CButton>
                </>
              )}
            {previewImprovementAction?.efficiency === null && is_director && (
              <CButton
                color="danger"
                size="sm"
                className="text-white"
                onClick={() => {
                  if (window.confirm("Tem a certeza que quer eliminar este registo?")) {
                    handleDelete(previewImprovementAction.id);
                  }
                }}
              >
                <CIcon icon={cilTrash} className="me-1" />
                Eliminar
              </CButton>
            )}
            <CButton
              color="link"
              className="text-body p-0"
              onClick={() => setPreviewImprovementAction(null)}
              title="Fechar"
            >
              <CIcon icon={cilX} />
            </CButton>
          </div>
        </COffcanvasHeader>
        <COffcanvasBody>
          {previewImprovementAction && (
            <div className="d-flex flex-column gap-3">
              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">Código</div>
                <div className="fw-semibold">{previewImprovementAction.code || "-"}</div>
              </div>
              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">Descrição NC/OM</div>
                <TextFieldCell html={previewImprovementAction.opportunity_description || "-"} />
              </div>
              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">Ação</div>
                <TextFieldCell html={previewImprovementAction.action || "-"} />
              </div>
              {[
                { label: "Origem/Âmbito", value: previewImprovementAction.origin },
                { label: "Fonte", value: previewImprovementAction.source },
                { label: "Código da fonte", value: previewImprovementAction.source_code },
                { label: "Data da fonte", value: previewImprovementAction.source_date },
                { label: "Prazo", value: previewImprovementAction.deadline },
                { label: "Data Conclusão", value: previewImprovementAction.conclusion_date },
                { label: "Data Avaliação", value: previewImprovementAction.evaluation_date },
                { label: "Avaliação", value: previewImprovementAction.efficiency },
              ].map((field) => (
                <div key={field.label} className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">{field.label}</div>
                  <div className="fw-semibold">{field.value || "-"}</div>
                </div>
              ))}
            </div>
          )}
        </COffcanvasBody>
      </COffcanvas>

      <CModal
        visible={postponeModalVisible}
        onClose={() => setPostponeModalVisible(false)}
      >
        <CModalHeader>Prorrogar Ação de Melhoria</CModalHeader>
        <CModalBody>
          <CFormInput
            type="date"
            label="Nova Data"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
          />

          <TextEditor
            label="Motivo"
            value={reason ?? ""}
            onChange={(value) => setReason(value)}
            className="mb-3"
          />
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            onClick={() => setPostponeModalVisible(false)}
          >
            Cancelar
          </CButton>
          <CButton color="primary" onClick={handlePostpone}>
            Confirmar
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default ImprovementAction;
